import React, { ReactElement, useContext, useState } from 'react'
// Commons imports.
import Wizard from '../Common/Components/Wizard'
import Loader from '../Common/Components/Loader'
import { WizardStep, WizardStepAction } from '../Common/Components/Wizard/Types'
// Sub imports.
import UploadStep from './UploadStep'
import ValidationStep from './ValidationStep'
import ResultStep from './ResultStep'
import { cancelCSVFile, confirmCSVFile, uploadCSVFile } from './API'
// Contexts imports.
import { LanguageContext, MessageContext, ServletPathContext } from '../Common/Contexts'
// Utilities imports.
import { LanguageBulkInsertUsers } from '../../../i18n'
import CLanguageHandler from '../../NonReact/Languages/LanguageHandler'
import { IsVarEmpty, isVarNotEmpty } from '../../NonReact/utils/GlobalFunctions'
import { CSVResultData } from './Types'
import { EmailErrorGeneric, GeneralErrorGeneric, User, ValidationErrorCSV } from '../Common/Types/Types'
// CSS imports. Commented out because of current build process.
// import './bulkInsertUsers.scss';

/**
 * Component properties type.
 */
type Props = {
    close: () => void
    refreshAction: () => void
    /** When true, wizard chrome matches routed admin modals (dark mode, button styling). */
    isRouterMode?: boolean
}

/**
 * New user wizard content component.
 *
 * @param close Callback on close.
 * @param refreshAction Refresh action callback to run on successful bulk insert.
 */
const BulkInsertWizard: React.FC<Props> = ({ close, refreshAction, isRouterMode }: Props): ReactElement => {
    const servletPath = useContext(ServletPathContext) as string
    const language = useContext(LanguageContext) as CLanguageHandler
    const { displayError } = useContext(MessageContext)
    const [isDataLoading, setDataLoading] = useState<boolean>(false)
    const [activeStepIndex, setActiveStepIndex] = useState<number>(0)
    const [file, setFile] = useState<OnlyNullable<File>>(null)
    const [errors, setErrors] = useState<Map<number, ValidationErrorCSV[]>>(new Map())
    const [generalErrorObj, setGeneralErrorObj] = useState<OnlyNullable<GeneralErrorGeneric>>(null)
    const [emailErrors, setEmailErrors] = useState<Map<number, EmailErrorGeneric[]>>(new Map())
    const [users, setUsers] = useState<User[]>([])

    // Whether the validation has been successful.
    const isValidationSuccessful = errors.size === 0 && IsVarEmpty(generalErrorObj)

    /**
     * Handles uploading start.
     */
    const handleUploadStart = async (): Promise<void> => {
        try {
            setDataLoading(true)

            setActiveStepIndex(1)

            if (IsVarEmpty(file)) return

            const bulkInsertResponse: CSVResultData = await uploadCSVFile(servletPath, file)

            if (bulkInsertResponse.Users) {
                setUsers(bulkInsertResponse.Users)
            }

            const generalError: Definable<GeneralErrorGeneric> = bulkInsertResponse.GeneralErrors

            if (generalError) {
                setGeneralErrorObj(generalError)
            }

            if (bulkInsertResponse.ValidationErrors) {
                const errorsFromResponse: ValidationErrorCSV[] = bulkInsertResponse.ValidationErrors
                const errorsMapByLineNumber = new Map<number, ValidationErrorCSV[]>()

                errorsFromResponse.forEach((error: ValidationErrorCSV): void => {
                    const errorsArray: ValidationErrorCSV[] = errorsMapByLineNumber.get(error.LineNumber) || []

                    errorsArray.push(error)
                    errorsMapByLineNumber.set(error.LineNumber, errorsArray)
                })

                setErrors(errorsMapByLineNumber)
            }
        } catch (bulkErrors: unknown) {
            displayError(bulkErrors instanceof Error ? bulkErrors : new Error(String(bulkErrors)))
        } finally {
            setDataLoading(false)
        }
    }

    /**
     * Retry resets.
     */
    const retryUpload = (): void => {
        try {
            setDataLoading(true)
            setFile(null)
            setErrors(new Map())
            setGeneralErrorObj(null)
            setUsers([])
            cancelCSVFile(servletPath)
        } catch (error) {
            // Failed cancelling the file.
            displayError(error as Error)
        } finally {
            setDataLoading(false)
            setActiveStepIndex(0)
        }
    }

    /**
     * Handles confirmation after successful validation.
     */
    const handleConfirm = async (): Promise<void> => {
        try {
            setDataLoading(true)
            const bulkInsertResponse: Definable<CSVResultData> = await confirmCSVFile(servletPath, true)

            if (IsVarEmpty(bulkInsertResponse)) return

            const confirmedResponse: CSVResultData = bulkInsertResponse

            setActiveStepIndex(2)

            const generalError = confirmedResponse.GeneralErrors
            if (generalError) {
                setGeneralErrorObj(generalError)
            }

            confirmedResponse.Users ? setUsers(confirmedResponse.Users) : setUsers([])

            if (confirmedResponse.EmailErrors) {
                const errorsFromResponse = confirmedResponse.EmailErrors
                const errorsMapByLineNumber = new Map<number, EmailErrorGeneric[]>()
                errorsFromResponse.forEach((emailError: EmailErrorGeneric) => {
                    const errorsArray = errorsMapByLineNumber.get(emailError.ErrorCode) || []
                    errorsArray.push(emailError)
                    errorsMapByLineNumber.set(emailError.ErrorCode, errorsArray)
                })

                setEmailErrors(errorsMapByLineNumber)
            }
        } catch (error) {
            retryUpload()
        } finally {
            setDataLoading(false)
        }
    }

    /**
     * Handles actions after last upload and closes modal.
     */
    const onCompleted = (): void => {
        close()
        refreshAction()
    }

    /**
     * Resolves validation step title based on the current process state.
     */
    const getValidationTitle = (): string => {
        let title = ''

        if (isDataLoading) {
            title = language?.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesSubheader2)
        } else if (!isValidationSuccessful) {
            title = language?.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesSubheader4)
        } else {
            title = language?.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesSubheader5)
        }

        return title
    }

    /**
     * Resolves validation step content based on the current process state.
     */
    const getValidationContent = (): ReactElement => {
        let content = null

        if (isDataLoading) {
            content = <Loader />
        } else {
            content = (
                <ValidationStep
                    generalError={generalErrorObj}
                    errors={errors}
                    users={users}
                    isRouterMode={isRouterMode}
                />
            )
        }

        return content
    }

    /**
     * Resolves validation step actions based on the current process state.
     */
    const getValidationActions = (): WizardStepAction[] => {
        let actions: WizardStepAction[] = []

        if (!isValidationSuccessful) {
            actions = [
                {
                    buttonText: language?.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesButton2),
                    onClick: retryUpload,
                    isEnabled: !isDataLoading,
                    isOutlined: true
                }
            ]
        } else {
            actions = [
                {
                    buttonText: language?.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesButton2),
                    onClick: retryUpload,
                    isEnabled: !isDataLoading,
                    isOutlined: true
                },
                {
                    buttonText: language?.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesButton3),
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onClick: handleConfirm,
                    isEnabled: !isDataLoading
                }
            ]
        }

        return actions
    }

    /**
     * Resolves confirmation step title based on the current process state.
     */
    const getConfirmTitle = (): string => {
        let title = ''

        if (isDataLoading) {
            title = language?.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesSubheader2)
        } else if (!isValidationSuccessful) {
            title = language?.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesSubheader4)
        } else {
            title = language?.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesSubheader6)
        }

        return title
    }

    /**
     * Resolves confirmation step content based on the current process state.
     */
    const getConfirmContent = (): ReactElement => {
        let content = null

        if (isDataLoading) {
            content = <Loader />
        } else if (generalErrorObj && emailErrors.size === 0) {
            content = (
                <ValidationStep
                    generalError={generalErrorObj}
                    errors={errors}
                    users={users}
                    isRouterMode={isRouterMode}
                />
            )
        } else {
            content = <ResultStep emailErrors={emailErrors} />
        }

        return content
    }

    /**
     * Resolves confirmation step actions based on the current process state.
     */
    const getConfirmActions = (): WizardStepAction[] => {
        let actions: WizardStepAction[] = []

        if (isDataLoading) {
            actions = [
                {
                    buttonText: language?.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesButton2),
                    onClick: retryUpload,
                    isEnabled: false
                }
            ]
        } else {
            actions = [
                {
                    buttonText: language?.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesButton4),
                    onClick: onCompleted,
                    isEnabled: !isDataLoading
                }
            ]
        }

        return actions
    }

    // Define wizard steps.
    const wizardSteps: WizardStep[] = [
        {
            title: language?.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesHeader1),
            subTitle: language?.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesSubheader1),
            content: <UploadStep file={file} setFile={setFile} />,
            actions: [
                {
                    buttonText: language?.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesButton1),
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onClick: handleUploadStart,
                    isEnabled: isVarNotEmpty(file)
                }
            ]
        },
        {
            title: language?.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesHeader1),
            subTitle: getValidationTitle(),
            content: getValidationContent(),
            actions: getValidationActions()
        },
        {
            title: language?.get(LanguageBulkInsertUsers.LanguageBulkInsertUsers_UploadfilesHeader1),
            subTitle: getConfirmTitle(),
            content: getConfirmContent(),
            actions: getConfirmActions()
        }
    ]

    return <Wizard wizardSteps={wizardSteps} activeStepIndex={activeStepIndex} isRouterMode={isRouterMode} />
}

export default BulkInsertWizard
